import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import SEOHead from "../components/SEOHead";
import GallerySection from "../components/GallerySection";
import SenjaWidget from "../components/SenjaWidget";
import MailerLiteWidget from "../components/MailerLiteWidget";
import { fetchCollection, fetchEntryByKey, getCompanyBadge } from "../lib/cms";

export default function Home() {
  const [pageData, setPageData] = useState(null);
  const [services, setServices] = useState([]);
  const [caseStudies, setCaseStudies] = useState([]);

  useEffect(() => {
    async function loadData() {
      const page = await fetchEntryByKey("pages", "page_key", "home");
      const serv = await fetchCollection("services");
      const cases = await fetchCollection("case_studies");
      setPageData(page);
      setServices(serv);
      setCaseStudies(cases);
    }
    loadData();
  }, []);

  const portraitUrl = "/_assets/profile.webp";

  return (
    <>
      <SEOHead
        title={pageData?.seo_title || "Prajwal Halwai | Freelance Web Developer and Software Developer"}
        description={pageData?.seo_description || "I build web applications, SaaS platforms, e-commerce platforms, and MVPs for founders and businesses worldwide. Fast to market, built to scale."}
        image={portraitUrl}
      />

      {/* HERO SECTION */}
      <section className="relative overflow-hidden pt-12 pb-20 md:py-28 border-b border-[#181B23]">
        {/* Subtle background glow */}
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[350px] bg-[#CC3737]/10 rounded-full blur-3xl pointer-events-none -z-10" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
            
            {/* Hero Text Column */}
            <div className="lg:col-span-7 space-y-6 text-left">
              {/* Trust Badge - Sole occurrence of 10+ years of experience */}
              <div className="inline-flex items-center gap-2.5 px-3.5 py-1.5 rounded-full bg-[#161A23] border border-[#262D3D] text-xs font-medium text-[#CBD5E1]">
                <span className="w-2 h-2 rounded-full bg-[#CC3737] animate-pulse" />
                <span>10+ years of experience building software for businesses of all sizes</span>
              </div>

              {/* Main Headline */}
              <h1 className="text-3xl sm:text-5xl lg:text-6xl font-extrabold text-white tracking-tight leading-[1.12]">
                Have an idea worth building? <br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-white via-slate-200 to-[#CC3737]">
                  Let's turn it into the right product.
                </span>
              </h1>

              {/* Supporting Copy */}
              <p className="text-base sm:text-lg text-[#94A3B8] leading-relaxed max-w-2xl font-normal">
                I help founders and businesses turn ideas into reliable web applications, SaaS products and digital platforms.
              </p>

              {/* Mindset Quote */}
              <div className="p-4 rounded-xl bg-[#11141C] border-l-2 border-[#CC3737] border-y border-r border-[#1E232F] text-xs sm:text-sm text-[#94A3B8] italic">
                "Whether you're a founder with an idea or a company with a problem, I help you ship the right thing without wasting time or starting over."
              </div>

              {/* Action Buttons */}
              <div className="pt-2 flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center gap-2.5 px-7 py-3.5 rounded-xl text-sm font-semibold text-white bg-[#CC3737] hover:bg-[#b52f2f] active:bg-[#9e2727] transition-all shadow-lg shadow-[#CC3737]/25 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#CC3737]"
                >
                  <span>Discuss your idea</span>
                  <i className="fa-solid fa-arrow-right text-xs" />
                </Link>
                <Link
                  to="/work"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl text-sm font-medium text-white bg-[#141720] hover:bg-[#1E232F] border border-[#272E3D] transition-all"
                >
                  <span>Explore my work</span>
                </Link>
              </div>

              {/* Quick Credibility Stats */}
              <div className="pt-6 grid grid-cols-3 gap-4 border-t border-[#1C202B]">
                <div>
                  <div className="text-xl sm:text-2xl font-bold text-white">Senior</div>
                  <div className="text-xs text-[#64748B]">Engineering Partner</div>
                </div>
                <div>
                  <div className="text-xl sm:text-2xl font-bold text-white">Direct</div>
                  <div className="text-xs text-[#64748B]">Owner Mindset</div>
                </div>
                <div>
                  <div className="text-xl sm:text-2xl font-bold text-white">Worldwide</div>
                  <div className="text-xs text-[#64748B]">Remote Delivery</div>
                </div>
              </div>
            </div>

            {/* Hero Image Column */}
            <div className="lg:col-span-5 flex justify-center lg:justify-end">
              <div className="relative w-full max-w-md">
                <div className="absolute -inset-2 rounded-3xl bg-gradient-to-tr from-[#CC3737]/20 to-transparent blur-md opacity-60 pointer-events-none" />
                
                <div className="relative rounded-2xl overflow-hidden bg-[#141720] border border-[#2B3242] shadow-2xl shadow-black/80">
                  <img
                    src={portraitUrl}
                    alt="Prajwal Halwai - Freelance Web Developer and Software Developer"
                    className="w-full h-auto aspect-[4/5] object-cover object-top"
                    loading="eager"
                  />
                  <div className="absolute bottom-0 inset-x-0 p-4 sm:p-5 bg-gradient-to-t from-[#0B0D11] via-[#0B0D11]/90 to-transparent">
                    <div>
                      <div className="text-sm font-bold text-white">Prajwal Halwai</div>
                      <div className="text-xs text-[#94A3B8]">Software Engineer & Product Partner</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* FOUNDER-FOCUSED SECTION */}
      <section className="py-20 sm:py-24 bg-[#0E1017] border-b border-[#181B23]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center space-y-4">
            <span className="text-xs font-semibold uppercase tracking-wider text-[#CC3737]">
              Start from where you are
            </span>
            <h2 className="text-2xl sm:text-4xl font-extrabold text-white tracking-tight">
              You don't need to have everything figured out.
            </h2>
            <p className="text-sm sm:text-base text-[#94A3B8] leading-relaxed">
              The best software products rarely begin with a 50-page technical specification. A good software project often starts with a problem worth solving.
            </p>
          </div>

          {(() => {
            const cards = [
              { title: "An idea", desc: "A novel concept you want to test in the market before competitors." },
              { title: "A business problem", desc: "Manual bottlenecks or friction points slowing down daily operations." },
              { title: "An inefficient process", desc: "Fragile spreadsheets and fragmented software that need consolidation." },
              { title: "A new opportunity", desc: "Expanding an existing service into a digital self-service platform." },
              { title: "A concept that needs validation", desc: "An MVP to gather authentic feedback and prove customer willingness to pay." }
            ];
            const remainder = cards.length % 3;

            return (
              <div className="mt-14 grid grid-cols-12 gap-4">
                {cards.map((card, idx) => {
                  let colSpanClass = "col-span-12 md:col-span-4";
                  if (remainder === 1 && idx === cards.length - 1) {
                    colSpanClass = "col-span-12 md:col-span-12";
                  } else if (remainder === 2 && idx >= cards.length - 2) {
                    colSpanClass = "col-span-12 md:col-span-6";
                  }

                  return (
                    <div
                      key={idx}
                      className={`${colSpanClass} p-5 rounded-xl bg-[#12151D] border border-[#1E232F] flex flex-col justify-between`}
                    >
                      <div>
                        <div className="w-8 h-8 rounded-lg bg-[#CC3737]/15 border border-[#CC3737]/25 text-[#CC3737] font-bold text-xs flex items-center justify-center mb-3">
                          0{idx + 1}
                        </div>
                        <h3 className="text-sm font-bold text-white mb-2">{card.title}</h3>
                        <p className="text-xs text-[#94A3B8] leading-relaxed">{card.desc}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })()}

          <div className="mt-10 p-6 rounded-2xl bg-[#151923] border border-[#252C3D] max-w-3xl mx-auto text-center space-y-3">
            <p className="text-sm text-white font-medium">
              Prajwal helps turn that starting point into a clear, realistic digital solution. Non-technical founders are welcome - we focus on your business goals first, technology second.
            </p>
            <div className="pt-2">
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 text-sm font-semibold text-[#CC3737] hover:underline"
              >
                <span>Tell me about your project</span>
                <i className="fa-solid fa-arrow-right text-xs" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CORE SERVICES OVERVIEW */}
      <section className="py-20 sm:py-28 border-b border-[#181B23]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-14 gap-6">
            <div>
              <span className="text-xs font-semibold uppercase tracking-wider text-[#CC3737]">
                What I build
              </span>
              <h2 className="text-2xl sm:text-4xl font-extrabold text-white tracking-tight mt-1.5">
                Core solutions architecture
              </h2>
              <p className="text-sm sm:text-base text-[#94A3B8] mt-2 max-w-xl">
                I help clients define and build solutions that make sense for the idea, users, and business.
              </p>
            </div>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 text-sm font-semibold text-[#CC3737] hover:underline shrink-0"
            >
              <span>Discuss your requirements</span>
              <i className="fa-solid fa-arrow-right text-xs" />
            </Link>
          </div>

          {/* 4 Primary Services Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {services.map((svc) => (
              <div
                key={svc.service_key}
                className="rounded-2xl bg-[#10131B] border border-[#202533] p-7 sm:p-8 flex flex-col justify-between"
              >
                <div>
                  <div className="mb-6">
                    <div className="w-16 h-16 sm:w-20 sm:h-20 flex items-center justify-start">
                      <img
                        src={Array.isArray(svc.icon) ? svc.icon[0] : svc.icon}
                        alt=""
                        className="w-full h-full object-contain"
                      />
                    </div>
                  </div>

                  <h3 className="text-xl sm:text-2xl font-bold text-white tracking-tight mb-3">
                    {svc.title}
                  </h3>

                  <p className="text-sm text-[#94A3B8] leading-relaxed mb-6">
                    {svc.short_description}
                  </p>

                  <div className="p-3.5 rounded-xl bg-[#151922] border border-[#202635] text-xs text-[#CBD5E1] mb-6 space-y-1">
                    <div className="font-semibold text-white">Ideal for:</div>
                    <div className="text-[#94A3B8]">{svc.audience}</div>
                  </div>
                </div>

                <div className="pt-4 border-t border-[#1C202C] flex items-center justify-between">
                  <Link
                    to="/contact"
                    className="inline-flex items-center gap-2 text-sm font-semibold text-[#CC3737] hover:text-[#e04343] transition-colors"
                  >
                    <span>Discuss this solution</span>
                    <i className="fa-solid fa-arrow-right text-xs" />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FROM IDEA TO PRODUCT - 4 STEP PROCESS */}
      <section className="py-20 sm:py-24 bg-[#0D0F14] border-b border-[#181B23]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-14">
            <span className="text-xs font-semibold uppercase tracking-wider text-[#CC3737]">
              Process
            </span>
            <h2 className="text-2xl sm:text-4xl font-extrabold text-white tracking-tight mt-1.5">
              From idea to product
            </h2>
            <p className="text-sm sm:text-base text-[#94A3B8] mt-2">
              Working with Prajwal makes software development feel manageable rather than complicated.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                step: "01",
                title: "Understand",
                desc: "Start with the idea, business challenge and intended users. Clarify what success looks like."
              },
              {
                step: "02",
                title: "Define",
                desc: "Clarify what should be built and what actually matters first. Strip away distraction features."
              },
              {
                step: "03",
                title: "Build",
                desc: "Turn the concept into a reliable working product. Clean code, documented architecture and rapid sprints."
              },
              {
                step: "04",
                title: "Improve",
                desc: "Learn from real usage and continue developing the product based on customer feedback."
              }
            ].map((p) => (
              <div
                key={p.step}
                className="p-6 rounded-2xl bg-[#11141C] border border-[#1E2431] relative"
              >
                <div className="text-3xl font-extrabold text-[#B91C1C] mb-4 font-mono">
                  {p.step}
                </div>
                <h3 className="text-lg font-bold text-white mb-2">{p.title}</h3>
                <p className="text-xs sm:text-sm text-[#94A3B8] leading-relaxed">
                  {p.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* INTRODUCTION VIDEO SECTION */}
      <section className="py-20 sm:py-24 bg-[#0A0C10] border-b border-[#181B23]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-10 space-y-2">
            <span className="text-xs font-semibold uppercase tracking-wider text-[#CC3737]">
              Introduction
            </span>
            <h2 className="text-2xl sm:text-4xl font-extrabold text-white tracking-tight">
              A quick introduction from Prajwal
            </h2>
            <p className="text-sm sm:text-base text-[#94A3B8]">
              Hear directly how I partner with founders and companies to engineer software.
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            {/* Desktop / Tablet Landscape 16:9 Video */}
            <div className="hidden sm:block rounded-2xl overflow-hidden bg-[#131620] border border-[#232938] shadow-2xl">
              <video
                controls
                playsInline
                preload="none"
                poster="/_assets/intro-desktop-poster.webp"
                className="w-full aspect-video object-cover"
              >
                <source src="/_assets/intro-desktop.mp4" type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            </div>

            {/* Mobile Vertical 9:16 Video */}
            <div className="sm:hidden rounded-2xl overflow-hidden bg-[#131620] border border-[#232938] shadow-2xl max-w-xs mx-auto">
              <video
                controls
                playsInline
                preload="none"
                poster="/_assets/intro-mobile-poster.webp"
                className="w-full aspect-[9/16] object-cover"
              >
                <source src="/_assets/intro-mobile.mp4" type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            </div>
          </div>
        </div>
      </section>

      {/* CREDIBILITY SECTION */}
      <section className="py-20 sm:py-28 border-b border-[#181B23]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            
            <div className="lg:col-span-5">
              <div className="rounded-2xl overflow-hidden bg-[#13161F] border border-[#252C3D] shadow-xl">
                <img
                  src="/_assets/working-1.webp"
                  alt="Prajwal Halwai at work"
                  className="w-full h-auto aspect-[4/3] object-cover"
                />
              </div>
            </div>

            <div className="lg:col-span-7 space-y-6">
              <span className="text-xs font-semibold uppercase tracking-wider text-[#CC3737]">
                Proven experience
              </span>
              <h2 className="text-2xl sm:text-4xl font-extrabold text-white tracking-tight">
                Building software with an owner's commercial mindset.
              </h2>
              <div className="space-y-4 text-sm sm:text-base text-[#94A3B8] leading-relaxed">
                <p>
                  I’ve spent years building software at small to large companies. I also help run an e-commerce business, from strategy to execution. That combination means I bring both engineering depth and an owner’s mindset to every project.
                </p>
                <p>
                  I understand what's at stake because I've been on your side of the table. Successful software development is not merely about writing syntactically correct code - it is about product thinking, understanding business requirements, solving problems, and building solutions that endure.
                </p>
              </div>

              {/* Three Authentic Principles */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-4">
                <div className="p-4 rounded-xl bg-[#11141C] border border-[#1E232F]">
                  <div className="font-bold text-white text-sm">Ship fast</div>
                  <div className="text-xs text-[#94A3B8] mt-1">Not in six months. In weeks. Faster delivery without shortcuts.</div>
                </div>
                <div className="p-4 rounded-xl bg-[#11141C] border border-[#1E232F]">
                  <div className="font-bold text-white text-sm">Build right</div>
                  <div className="text-xs text-[#94A3B8] mt-1">Speed without compromising quality. Decisions made with scale from day one.</div>
                </div>
                <div className="p-4 rounded-xl bg-[#11141C] border border-[#1E232F]">
                  <div className="font-bold text-white text-sm">Grow from there</div>
                  <div className="text-xs text-[#94A3B8] mt-1">The MVP is the start, not the finish. Built to scale as users grow.</div>
                </div>
              </div>

              <div className="pt-2">
                <Link
                  to="/about"
                  className="inline-flex items-center gap-2 text-sm font-semibold text-[#CC3737] hover:underline"
                >
                  <span>Read more about my background</span>
                  <i className="fa-solid fa-arrow-right text-xs" />
                </Link>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* FEATURED WORK & CASES */}
      <section className="py-20 sm:py-24 bg-[#0C0E14] border-b border-[#181B23]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between mb-12 gap-4">
            <div>
              <span className="text-xs font-semibold uppercase tracking-wider text-[#CC3737]">
                SELECTED PROJECTS
              </span>
              <h2 className="text-2xl sm:text-4xl font-extrabold text-white tracking-tight mt-1.5">
                Featured work
              </h2>
            </div>
            <Link
              to="/work"
              className="inline-flex items-center gap-2 text-sm font-semibold text-[#CC3737] hover:underline"
            >
              <span>View all projects</span>
              <i className="fa-solid fa-arrow-right text-xs" />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {caseStudies.slice(0, 4).map((cs) => {
              const badge = getCompanyBadge(cs);
              return (
                <div
                  key={cs.case_key || cs.id}
                  className="rounded-2xl bg-[#11141D] border border-[#222837] p-6 sm:p-8 flex flex-col justify-between"
                >
                  <div>
                    {badge && (
                      <div className="mb-3">
                        <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold tracking-wide border ${badge.className}`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${badge.dotClassName}`} />
                          {badge.label}
                        </span>
                      </div>
                    )}
                    <h3 className="text-xl font-bold text-white mb-3">{cs.title}</h3>
                    {cs.client && <div className="text-xs text-[#64748B] mb-4">Client: {cs.client}</div>}
                    
                    <div className="text-xs sm:text-sm text-[#94A3B8] space-y-2 mb-6">
                      {cs.solution ? (
                        <div 
                          dangerouslySetInnerHTML={{ __html: cs.solution }}
                          className="[&>p]:leading-relaxed"
                        />
                      ) : cs.approach ? (
                        <div 
                          dangerouslySetInnerHTML={{ __html: cs.approach }}
                          className="[&>p]:leading-relaxed"
                        />
                      ) : null}
                    </div>
                  </div>

                  <div className="pt-4 border-t border-[#1D222E] flex justify-between items-center">
                    <Link
                      to="/work"
                      className="inline-flex items-center gap-2 text-sm font-semibold text-[#CC3737] hover:underline"
                    >
                      <span>View project details</span>
                      <i className="fa-solid fa-arrow-right text-xs" />
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CONNECTED CMS GALLERI COMPONENT */}
      <GallerySection
        gallerySlug="home-gallery"
        title="Workspace & Project Artifacts"
        subtitle="Visual snapshot from active software architecture and client projects."
      />

      {/* TESTIMONIALS - SENJA WIDGET */}
      <SenjaWidget />

      {/* FINAL CONVERSION SECTION */}
      <section className="py-20 sm:py-28">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-6">
          <span className="text-xs font-semibold uppercase tracking-wider text-[#CC3737]">
            Ready to start?
          </span>
          <h2 className="text-3xl sm:text-5xl font-extrabold text-white tracking-tight">
            Have an idea worth building? Let's turn it into the right product.
          </h2>
          <p className="text-base text-[#94A3B8] max-w-xl mx-auto">
            Book a discovery call or tell me about your project. I'll listen, ask the right questions, and tell you if I can help.
          </p>
          <div className="pt-2 flex flex-col sm:flex-row justify-center items-center gap-4">
            <Link
              to="/contact"
              className="px-8 py-4 rounded-xl text-base font-semibold text-white bg-[#CC3737] hover:bg-[#b52f2f] transition-all shadow-xl shadow-[#CC3737]/25"
            >
              Discuss your idea
            </Link>
            <a
              href="tel:+4550382138"
              className="px-6 py-4 rounded-xl text-base font-medium text-white bg-[#141720] hover:bg-[#1E2432] border border-[#272E3E] transition-all whitespace-nowrap"
            >
              Call +45 50 38 21 38
            </a>
          </div>
        </div>
      </section>

      {/* MAILERLITE EMBEDDED NEWSLETTER SECTION */}
      <MailerLiteWidget formId="jSUyFj" accountId="2193986" />
    </>
  );
}
